export type Id = string;

export interface Entity {
  id: Id;
  createdAt?: Date;
  updatedAt?: Date;
  deleted?: boolean;
}

export interface Translation {
  es: string;
  en: string;
  fr: string;
  it: string;
  pt: string;
}

export interface TranslationArray {
  es?: string[];
  en?: string[];
  fr?: string[];
  it?: string[];
  pt?: string[];
}

export const MASTER_VALUE_OTHER_ID: Id = 'OTHER';

export type Language = 'es' | 'en' | 'fr' | 'it' | 'pt';
export type Role = 'admin' | 'employee' | 'booking' | 'media' | 'organization' | 'mechanic';

export const EDITION_TYPES = ['maroc', 'tunisie'];
export type EditionType = (typeof EDITION_TYPES)[number];

export type PaymentMethod = 'REDSYS' | 'BANK';

export const PAYMENT_STATUSES = ['PENDING', 'FAIL', 'INSCRIPTION_PENDING', 'PARTIAL_PAID', 'PAID'];
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const BILLING_OPTIONS = ['IVA', 'INTRA', 'NO'];
export type BillingOption = (typeof BILLING_OPTIONS)[number];

export const INSCRIPTIONS_STATUTES = ['DRAFT', 'PENDING', 'ACCEPTED', 'REJECTED'];
export type InscriptionStatus = (typeof INSCRIPTIONS_STATUTES)[number];

export const LEVELS = ['ROOKIE', 'EXPERT', 'PRO', 'LEGEND', 'ORGANIZATION'];
export type Level = (typeof LEVELS)[number];

export const VEHICLE_BRANDS = [
  'ALFA_ROMEO',
  'ASIA_MOTORS',
  'AUDI',
  'BMW',
  'CAN_AM',
  'CF_MOTO',
  'CHEVROLET',
  'CITROEN',
  'DACIA',
  'DAEWOO',
  'DAIHATSU',
  'DATSUN',
  'DR',
  'FIAT',
  'FORD',
  'GALLOPER',
  'HONDA',
  'HUMMER',
  'HYUNDAI',
  'INEOS',
  'ISUZO',
  'IVECO',
  'JEEP',
  'KIA',
  'LADA',
  'LANCIA',
  'LAND_ROVER',
  'LEXUS',
  'MAZDA',
  'MERCEDES',
  'MITSUBISHI',
  'NISSAN',
  'OPEL',
  'PEUGEOT',
  'POLARIS',
  'PORSCHE',
  'RENAULT',
  'ROVER',
  'SAAB',
  'SANTANA',
  'SEAT',
  'SKODA',
  'SMART',
  'SSANGYONG',
  'SUBARU',
  'SUZUKI',
  'TATA',
  'TOYOTA',
  'UMM',
  'VOLVO',
  'WV',
  'YAMAHA',
  'OTHER',
];
export type VehicleBrand = (typeof VEHICLE_BRANDS)[number];

export const VEHICLE_BRAND_LABELS: Record<VehicleBrand, string> = {
  ALFA_ROMEO: 'Alfa Romeo',
  ASIA_MOTORS: 'Asia Motors',
  AUDI: 'Audi',
  BMW: 'BMW',
  CAN_AM: 'Can-Am',
  CF_MOTO: 'CF Moto',
  CHEVROLET: 'Chevrolet',
  CITROEN: 'Citroën',
  DACIA: 'Dacia',
  DAEWOO: 'Daewoo',
  DAIHATSU: 'Daihatsu',
  DATSUN: 'Datsun',
  DR: 'DR',
  FIAT: 'Fiat',
  FORD: 'Ford',
  GALLOPER: 'Galloper',
  HONDA: 'Honda',
  HUMMER: 'Hummer',
  HYUNDAI: 'Hyundai',
  INEOS: 'Ineos',
  ISUZO: 'Isuzu',
  IVECO: 'Iveco',
  JEEP: 'Jeep',
  KIA: 'Kia',
  LADA: 'Lada',
  LANCIA: 'Lancia',
  LAND_ROVER: 'Land Rover',
  LEXUS: 'Lexus',
  MAZDA: 'Mazda',
  MERCEDES: 'Mercedes',
  MITSUBISHI: 'Mitsubishi',
  NISSAN: 'Nissan',
  OPEL: 'Opel',
  PEUGEOT: 'Peugeot',
  POLARIS: 'Polaris',
  PORSCHE: 'Porsche',
  RENAULT: 'Renault',
  ROVER: 'Rover',
  SAAB: 'Saab',
  SANTANA: 'Santana',
  SEAT: 'Seat',
  SKODA: 'Škoda',
  SMART: 'Smart',
  SSANGYONG: 'SsangYong',
  SUBARU: 'Subaru',
  SUZUKI: 'Suzuki',
  TATA: 'Tata',
  TOYOTA: 'Toyota',
  UMM: 'UMM',
  VOLVO: 'Volvo',
  WV: 'Volkswagen',
  YAMAHA: 'Yamaha',
  OTHER: 'Other',
};

export const STAGE_TYPES = ['LINK', 'TRANSPORT', 'STAGE', 'ORGANIZATION', 'EVENT'] as const;

export type StageType = (typeof STAGE_TYPES)[number];

export const ITEM_TYPES = ['FERRY', 'HOTEL'] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

export const STOCK_TYPES = ['SINGLE', 'TWIN', 'DOUBLE', 'TRIPLE', 'CUADRUPLE'] as const;

export type StockType = (typeof STOCK_TYPES)[number];

export const STOCK_FERRY_TYPES = [
  'VEHICLE',
  'VEHICLE_190',
  'VEHICLE_191_260',
  'REMOLQUE_6M',
  'BUTACA',
  'CAMAROTE_INDIVIDUAL',
  'CAMAROTE_DOBLE',
  'CAMAROTE_TRIPLE',
  'CAMAROTE_CUADRUPLE_SHARED',
  'MEDIO_CAMAROTE_CUADRUPLE_SHARED',
] as const;

export type StockFerryType = (typeof STOCK_FERRY_TYPES)[number];

/** Cómo se llaman las plazas para quien trabaja con ellas, no como las guardamos. */
export const STOCK_TYPE_LABELS: Record<string, string> = {
  SINGLE: 'Individual',
  TWIN: 'Twin (camas separadas)',
  DOUBLE: 'Doble',
  TRIPLE: 'Triple',
  CUADRUPLE: 'Cuádruple',
  VEHICLE: 'Vehículo',
  VEHICLE_190: 'Vehículo pequeño ≤ 1,90m',
  VEHICLE_191_260: 'Vehículo grande 1,91m hasta 2,60m',
  REMOLQUE_6M: 'Remolque ≤ 6m',
  BUTACA: 'Butaca',
  CAMAROTE_INDIVIDUAL: 'Camarote Individual',
  CAMAROTE_DOBLE: 'Camarote Doble',
  CAMAROTE_TRIPLE: 'Camarote Triple',
  CAMAROTE_CUADRUPLE_SHARED: 'Camarote Cuádruple Compartido',
  MEDIO_CAMAROTE_CUADRUPLE_SHARED: '1/2 Camarote Cuádruple Compartido',
};
